"use client";

import { usePathname, useSearchParams } from "next/navigation";

/**
 * Builds an SSR-safe, crawlable href for a paginated page control.
 *
 * Mirrors `useNavigate().pushQueryParam` semantics — preserves every existing
 * query param and only updates the given page key — but resolves the path via
 * `usePathname()`/`useSearchParams()` instead of `window.location`, so the
 * href is correct during server rendering (and for dynamic routes such as the
 * organisation tab pages).
 *
 * `excludeKeys` drops transient params (e.g. `not_found`) from the built href.
 */
export function usePageHref(): (
  pageKey: string,
  pageNumber: number,
  excludeKeys?: string[]
) => string {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  return (
    pageKey: string,
    pageNumber: number,
    excludeKeys: string[] = []
  ): string => {
    const qs = new URLSearchParams(searchParams?.toString() ?? "");
    excludeKeys.forEach((key) => qs.delete(key));
    qs.set(pageKey, String(pageNumber));
    return `${pathname}?${qs.toString()}`;
  };
}
